/**
 * The customer app's calls. Everything goes through @aseed/api-client, which
 * owns CSRF, idempotency keys and the error envelope; this module only knows
 * which calls the vending journey makes.
 *
 * Idempotency: each logical action gets ONE key, reused if the customer retries
 * that same action (a double tap or a retry after a dropped connection must not
 * open a second order or a second payment). See `ActionKeys`.
 */
import { AseedApiClient, ApiError, NetworkError, newIdempotencyKey } from "@aseed/api-client";
import { store } from "./storage";
import type { Channel, VendSession } from "./vend";

export { ApiError, NetworkError };

export const client = new AseedApiClient({ getCsrfToken: () => store.get("csrf") });

/** Keys per logical action, reused across retries of that action only. */
export class ActionKeys {
  private readonly keys = new Map<string, string>();

  for(action: string): string {
    let key = this.keys.get(action);
    if (!key) {
      key = newIdempotencyKey();
      this.keys.set(action, key);
    }
    return key;
  }

  /** The action finished (or was abandoned): a new attempt is a new action. */
  done(action: string): void {
    this.keys.delete(action);
  }
}

async function startGuestSession(): Promise<void> {
  // The server issues the identity and sets the HttpOnly cookie; the CSRF token
  // is the only thing this page keeps.
  const issued = await client.post<{ csrf_token?: string | null }>(
    "/sessions",
    {},
    { idempotencyKey: newIdempotencyKey() },
  );
  if (issued.csrf_token) store.set("csrf", issued.csrf_token);
}

function isSessionProblem(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 401 || e.code === "csrf_failed");
}

/** Run `call` with a session, starting a guest one if there is none or it lapsed. */
async function withSession<T>(call: () => Promise<T>): Promise<T> {
  if (!store.get("csrf")) await startGuestSession();
  try {
    return await call();
  } catch (e) {
    if (!isSessionProblem(e)) throw e;
    await startGuestSession();
    return call();
  }
}

export function openVisit(qrCode: string, channel: Channel, idempotencyKey: string): Promise<VendSession> {
  // A new session is only safe here, before any visit exists: a visit belongs
  // to the session that opened it, so later calls must not swap sessions.
  return withSession(() =>
    client.post<VendSession>("/vend-sessions", { qr_code: qrCode, channel }, { idempotencyKey }),
  );
}

export function getVisit(id: string, signal?: AbortSignal): Promise<VendSession> {
  return client.get<VendSession>(`/vend-sessions/${encodeURIComponent(id)}`, signal ? { signal } : {});
}

export function payVisit(id: string, idempotencyKey: string): Promise<VendSession> {
  return client.post<VendSession>(`/vend-sessions/${encodeURIComponent(id)}/pay`, undefined, { idempotencyKey });
}

export function cancelVisit(id: string, idempotencyKey: string): Promise<VendSession> {
  return client.post<VendSession>(`/vend-sessions/${encodeURIComponent(id)}/cancel`, undefined, { idempotencyKey });
}

export function refundVisit(id: string, idempotencyKey: string): Promise<VendSession> {
  return client.post<VendSession>(`/vend-sessions/${encodeURIComponent(id)}/refund`, undefined, { idempotencyKey });
}

export function eventsUrl(id: string): string {
  return `/api/v1/vend-sessions/${encodeURIComponent(id)}/events`;
}

/** The catalogue key that explains an error to the customer. */
export function errorKey(e: unknown): string {
  if (e instanceof NetworkError) return "vend.error.network";
  // A proxy's HTML error page: the platform could not be reached at all.
  if (e instanceof ApiError && e.code === "non_json_response" && e.status >= 502) return "vend.error.network";
  if (e instanceof ApiError) {
    const specific: Record<string, string> = {
      machine_unavailable: "vend.error.machine_unavailable",
      machine_rejected: "vend.error.machine_rejected",
      vend_unavailable: "vend.error.vend_unavailable",
      selection_not_payable: "vend.error.selection_not_payable",
      payment_rejected: "vend.error.payment_rejected",
      payment_page_missing: "vend.error.payment_page_missing",
      refund_rejected: "vend.error.refund_rejected",
      dependency_unavailable: "error.dependency_unavailable",
      not_found: "vend.error.session_lost",
      unauthorized: "vend.error.session_lost",
    };
    if (specific[e.code]) return specific[e.code] as string;
    if (e.status === 401 || e.status === 404) return "vend.error.session_lost";
    if (e.messageKey?.startsWith("error.")) return e.messageKey;
  }
  return "vend.error.generic";
}
